<script setup>
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch,
} from 'vue';
import {
  formEventsUrl,
  getFormPage,
  getFormPayment,
  partialFormSubmitUrl,
  submitForm,
  submitPartialForm,
  trackFormEvents,
} from './api/forms';
import {
  buildThemeStyle,
  resolveHeaderImageUrl,
  resolveLogoUrl,
} from './helpers/theme';
import { useFormSteps } from './helpers/useFormSteps';
import { isFieldVisible } from './helpers/conditions';
import { captureTracking } from './tracking';
import FormField from './components/FormField.vue';

const CONSENT_FALLBACK_KEY = 'form_consent_accepted';
const DEFAULT_SUCCESS_MESSAGE =
  'Recebemos seus dados! Em breve entramos em contato.';

// step: 'loading' | 'invalid' | 'form' | 'success'
const step = ref('loading');
const page = ref(null);
const values = reactive({});
const errors = reactive({});
const hp = ref('');
const submitting = ref(false);
const banner = ref(null); // { type: 'error' | 'rate_limit', message }
const successMessage = ref(DEFAULT_SUCCESS_MESSAGE);
// Retorno do campo de agendamento: o servidor tenta marcar DEPOIS de salvar o
// lead, então aqui só relatamos o que aconteceu. Horário tomado no meio do
// caminho não é erro de envio — o lead está salvo e alguém vai chamar.
const agendamento = ref(null);
// Pix do envio: nasce 'pending' com um token e vira 'ready' quando o Asaas
// devolve o QR. Consultamos por um tempo limitado — cobrança que não sai em
// meio minuto vira instrução de contato, não uma roda girando para sempre.
const pagamento = ref(null);
const PIX_TENTATIVAS = 15;
const PIX_INTERVALO = 2500;
let pixTimer = null;
const mounted = ref(false);
const stepFormRef = ref(null);
// Metadados de rastreamento (UTMs/referrer/fbp/fbc/params extras), capturados
// na carga conforme a config do form e enviados junto do submit.
const tracking = ref({});

// Partial lead capture — see savePartial()/savePartialBeacon() below. Tracks
// whether the real submit already succeeded (no partial saves after that)
// and a snapshot of the last-sent partial payload (skip redundant sends).
const fullySubmitted = ref(false);
const lastPartialSnapshot = ref(null);
const partialCaptureEnabled = computed(
  () => page.value?.tracking_config?.partial_capture_enabled !== false
);

// Customizable success-screen title. Same "no migration" trick as
// theme.layout/theme.welcome: lives inside theme (JSONB), falls back to a
// sensible default when the account never set one.
// Uma frase, no fuso de quem agenda, dizendo o que ficou marcado — ou por que
// não ficou. Silêncio aqui deixaria o lead achando que marcou quando não marcou.
const agendamentoTexto = computed(() => {
  const dados = agendamento.value;
  if (!dados) return '';
  if (dados.status === 'confirmed') {
    const quando = new Intl.DateTimeFormat('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(new Date(dados.scheduled_at));
    const servico = dados.service_name ? ` (${dados.service_name})` : '';
    return `Horário confirmado: ${quando}${servico}.`;
  }
  if (dados.reason === 'slot_taken') {
    return 'O horário que você escolheu acabou de ser reservado. Recebemos os seus dados e vamos te chamar para remarcar.';
  }
  return 'Recebemos os seus dados. Vamos confirmar o horário com você.';
});

const successTitle = computed(
  () => page.value?.theme?.success_title || 'Tudo certo!'
);

// Foca o input do passo atual pra quem responde já sair digitando (Typeform).
// Ignora checkbox/radio (não são "digitáveis" e roubariam o foco visual).
const focusStepInput = () => {
  nextTick(() => {
    const el = stepFormRef.value?.querySelector(
      'input:not([type="checkbox"]):not([type="radio"]), textarea, select'
    );
    if (el && typeof el.focus === 'function') el.focus();
  });
};

const slug = window.formSlug || '';

const acompanharPix = (tentativa = 0) => {
  const token = pagamento.value?.token;
  if (!token || pagamento.value.status === 'ready') return;
  if (tentativa >= PIX_TENTATIVAS) {
    pagamento.value = { ...pagamento.value, status: 'failed' };
    return;
  }
  pixTimer = setTimeout(async () => {
    try {
      const dados = await getFormPayment({ slug, token });
      if (dados.status === 'ready') {
        pagamento.value = { ...pagamento.value, ...dados, status: 'ready' };
        return;
      }
      if (dados.status === 'failed') {
        pagamento.value = { ...pagamento.value, status: 'failed' };
        return;
      }
    } catch {
      // Consulta que falhou não é cobrança que falhou: seguimos tentando.
    }
    acompanharPix(tentativa + 1);
  }, PIX_INTERVALO);
};

const pixCopiado = ref(false);
const copiarPix = async () => {
  try {
    await navigator.clipboard.writeText(pagamento.value.pix_payload);
    pixCopiado.value = true;
    setTimeout(() => {
      pixCopiado.value = false;
    }, 2500);
  } catch {
    pixCopiado.value = false;
  }
};

const pixValor = computed(() =>
  pagamento.value?.value
    ? `R$ ${Number(pagamento.value.value).toFixed(2).replace('.', ',')}`
    : ''
);

/**
 * Avisa a página que hospeda o formulário, quando ele roda embutido num iframe.
 * Embutido, o pixel de conversão mora na página de fora, não aqui — sem esse
 * aviso ela só saberia que o formulário foi ABERTO, e contaria como lead quem
 * apenas espiou. Mandamos junto o event_id para a Meta seguir deduplicando o
 * Pixel contra o CAPI.
 *
 * Alvo '*' porque o payload não carrega nada sensível (slug + evento). Quem
 * escuta é que deve conferir a origem antes de acreditar.
 */
const notifyHost = payload => {
  if (window.parent === window) return;

  try {
    window.parent.postMessage({ chatsuiteForm: slug, ...payload }, '*');
  } catch (error) {
    /* página de fora inacessível: seguir sem interromper o envio */
  }
};

const isBlank = value =>
  value === undefined || value === null || String(value).trim() === '';

const fields = computed(() => page.value?.fields || []);
// F3.5 — só os campos VISÍVEIS dado o estado atual das respostas (condicional).
// Alimenta os passos, o render de página única e a validação: campo escondido
// não conta no progresso nem exige preenchimento.
const visibleFields = computed(() =>
  fields.value.filter(field => isFieldVisible(field, values))
);

// Resposta de pergunta ESCONDIDA não vai no envio. Se o lead voltou e mudou a
// resposta que fazia a pergunta aparecer, o que ele digitou antes deixou de
// valer, e mandar assim mesmo enche o CRM de resposta que ninguém deu. O caso
// que dói de verdade é o agendamento: um horário velho marcaria uma reunião que
// o lead não pediu. Chave que não é campo declarado passa direto.
const respostasValidas = () => {
  const declaradas = new Set(fields.value.map(item => item.key));
  const visiveis = new Set(visibleFields.value.map(item => item.key));
  return Object.fromEntries(
    Object.entries(values).filter(
      ([chave]) => !declaradas.has(chave) || visiveis.has(chave)
    )
  );
};

// A partial save only makes sense once there is *something* to dedupe a lead
// off of — the ingestion service keys off phone (then email), same as a full
// submit. Field keys are author-defined, so we identify them by declared
// `type` rather than assuming a fixed key name like "phone"/"email".
const identifierFieldKeys = computed(() =>
  fields.value
    .filter(field => field.type === 'phone' || field.type === 'email')
    .map(field => field.key)
);
const hasIdentifierValue = computed(() =>
  identifierFieldKeys.value.some(key => !isBlank(values[key]))
);
const themeStyle = computed(() => buildThemeStyle(page.value?.theme || {}));
const logoUrl = computed(() => resolveLogoUrl(page.value?.theme || {}));
const headerImageUrl = computed(() =>
  resolveHeaderImageUrl(page.value?.theme || {})
);
// A imagem de topo é, por padrão, um "hero" da tela de boas-vindas — NÃO se
// repete nas perguntas. O logo (resolveLogoUrl) é a marca persistente e aparece
// em todas as telas. Só quando theme.header_on_form=true a imagem de topo também
// aparece dentro do formulário.
const formHeaderImageUrl = computed(() =>
  page.value?.theme?.header_on_form ? headerImageUrl.value : ''
);
// Layout is a rendering choice only — same `fields`/submission contract either
// way. Stored under theme.layout so no schema migration was needed for it.
const isStepsLayout = computed(() => page.value?.theme?.layout === 'steps');

// Optional intro/cover screen shown before any question, SpotForm/Typeform
// style. Lives under theme.welcome (JSONB) — same "no migration needed"
// trick as theme.layout above. Independent of layout: it precedes both the
// single-page and the steps experience.
const welcome = computed(() => page.value?.theme?.welcome || {});
const showWelcome = ref(false);
const welcomeButtonLabel = computed(
  () => welcome.value.button_text || 'Iniciar'
);

const dismissWelcome = () => {
  showWelcome.value = false;
  focusStepInput();
};

// The welcome screen has no text input to submit via the browser's native
// Enter-submits-a-form behavior, so Enter is wired explicitly while it's on
// screen (removed again once dismissed, so it never fights the steps
// layout's own Enter-to-advance handling underneath it).
const onWelcomeKeydown = event => {
  if (event.key === 'Enter') {
    event.preventDefault();
    dismissWelcome();
  }
};

watch(showWelcome, isShown => {
  if (isShown) {
    window.addEventListener('keydown', onWelcomeKeydown);
  } else {
    window.removeEventListener('keydown', onWelcomeKeydown);
  }
});

onUnmounted(() => {
  if (pixTimer) clearTimeout(pixTimer);
  window.removeEventListener('keydown', onWelcomeKeydown);
});

const hasInlineConsentField = computed(() =>
  fields.value.some(field => field.type === 'consent')
);
const showStandaloneConsent = computed(
  () => !!page.value?.consent?.enabled && !hasInlineConsentField.value
);
const consentText = computed(
  () =>
    page.value?.consent?.text ||
    'Concordo em ser contatado(a) com base nos dados enviados.'
);

const defaultValueFor = field => {
  if (field.type === 'checkbox') return [];
  if (field.type === 'consent') return false;
  return '';
};

const initializeValues = () => {
  fields.value.forEach(field => {
    values[field.key] = defaultValueFor(field);
  });
  if (showStandaloneConsent.value) {
    values[CONSENT_FALLBACK_KEY] = false;
  }
};

// Single-field validation rule, shared by the whole-page validate() (single
// layout / final-step submit) and by the per-step gate that blocks "Próximo"
// on the steps layout — one set of messages either way.
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const fieldError = field => {
  const value = values[field.key];

  if (field.type === 'checkbox') {
    if (field.required && !(Array.isArray(value) && value.length > 0)) {
      return 'Selecione ao menos uma opção.';
    }
    return '';
  }
  if (field.type === 'consent') {
    if (field.required && value !== true) {
      return 'É preciso aceitar para continuar.';
    }
    return '';
  }

  const blank = isBlank(value);
  if (field.required && blank) return 'Campo obrigatório.';
  // Validação de FORMATO por tipo — vale mesmo em campo opcional que foi
  // preenchido (antes o email/telefone aceitava qualquer texto).
  if (blank) return '';

  const text = String(value).trim();
  if (field.type === 'email' && !EMAIL_RE.test(text)) {
    return 'Informe um email válido.';
  }
  if (field.type === 'phone' && text.replace(/\D/g, '').length < 8) {
    return 'Informe um telefone válido.';
  }
  if (field.type === 'number' && Number.isNaN(Number(text))) {
    return 'Informe um número válido.';
  }
  return '';
};

const validateFields = fieldList => {
  let ok = true;
  fieldList.forEach(field => {
    const message = fieldError(field);
    if (message) {
      errors[field.key] = message;
      ok = false;
    } else {
      delete errors[field.key];
    }
  });
  return ok;
};

// Steps layout: the field's own label becomes the big on-screen question
// (rendered by the template, not FormField), so the helper line below it
// uses field.description when present, falling back to the placeholder. When
// the placeholder is "used up" as that helper line, it must not also show up
// inside the input — bareField() below strips it in that case only.
const stepHelperText = field => field.description || field.placeholder || '';

const bareField = field => ({
  ...field,
  placeholder: field.description ? field.placeholder : '',
});

const validate = () => {
  const fieldsOk = validateFields(visibleFields.value);

  delete errors[CONSENT_FALLBACK_KEY];
  let consentOk = true;
  if (showStandaloneConsent.value && values[CONSENT_FALLBACK_KEY] !== true) {
    errors[CONSENT_FALLBACK_KEY] = 'É preciso aceitar para continuar.';
    consentOk = false;
  }

  return fieldsOk && consentOk;
};

const {
  currentIndex: stepIndex,
  totalSteps,
  currentStepFields,
  isFirstStep: isFirstFormStep,
  isLastStep: isLastFormStep,
  progress: stepProgress,
  goNext: goNextStep,
  goPrev: goPrevStep,
  reset: resetSteps,
} = useFormSteps({ fields: visibleFields, canAdvance: validateFields });

// Snapshot used to dedupe partial saves — only the answer values matter (not
// the honeypot, which stays empty for real visitors), so a step advance or a
// beacon that carries no new information never fires a redundant request.
const partialSnapshot = () => JSON.stringify(values);

// Fire-and-forget draft save, called after a step advance. Never blocks the
// UI and never surfaces an error — losing a partial save silently is far
// better than interrupting someone mid-form over it.
const savePartial = () => {
  if (
    !partialCaptureEnabled.value ||
    fullySubmitted.value ||
    !hasIdentifierValue.value
  )
    return;
  const snapshot = partialSnapshot();
  if (snapshot === lastPartialSnapshot.value) return;
  lastPartialSnapshot.value = snapshot;

  submitPartialForm({ slug, fields: respostasValidas(), hp: hp.value }).catch(
    () => {}
  );
};

// Best-effort save for the "the tab is going away" moments (background/close/
// navigate). A regular fetch/axios call can get cancelled mid-flight when the
// page unloads, so these two triggers specifically use
// navigator.sendBeacon, which is designed to survive that.
const savePartialBeacon = () => {
  if (
    !partialCaptureEnabled.value ||
    fullySubmitted.value ||
    !hasIdentifierValue.value
  )
    return;
  if (typeof navigator?.sendBeacon !== 'function') return;
  const snapshot = partialSnapshot();
  if (snapshot === lastPartialSnapshot.value) return;
  lastPartialSnapshot.value = snapshot;

  const payload = JSON.stringify({
    fields: respostasValidas(),
    hp: hp.value,
    partial: true,
  });
  const blob = new Blob([payload], { type: 'application/json' });
  navigator.sendBeacon(partialFormSubmitUrl(slug), blob);
};

// F3.2 — analytics anônima. session: uuid do visitante (só agrupa eventos da
// mesma sessão, sem identificar ninguém). Emitimos view/start/step_completed/
// submit num lote com flush debounced; no unload vai por sendBeacon.
const newSessionToken = () => {
  if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
  return `s-${Date.now()}-${Math.random().toString(36).slice(2)}`;
};
const sessionToken = newSessionToken();
const pendingEvents = [];
const startTracked = ref(false);
let flushTimer = null;

// F3.4 — teste A/B. A variante é escolhida de forma ESTÁVEL por sessão
// (hash do session token) sobre [controle 'A', ...variantes] e os overrides de
// conteúdo são aplicados na config. Todos os eventos vão marcados com a variante
// pra medir conversão por variante no painel Resultados.
const activeVariantKey = ref('A');
const hashToInt = str => {
  let h = 0;
  for (let i = 0; i < str.length; i += 1) {
    h = (h * 31 + str.charCodeAt(i)) % 2147483647;
  }
  return h;
};
const applyVariant = () => {
  const list = page.value?.variants || [];
  if (!list.length) return;
  const idx = hashToInt(sessionToken) % (list.length + 1);
  if (idx === 0) return; // controle 'A' — sem override
  const v = list[idx - 1] || {};
  activeVariantKey.value = v.key || String.fromCharCode(66 + (idx - 1));
  const theme = { ...(page.value.theme || {}) };
  if (v.primary_color) {
    theme.primary_color = v.primary_color;
    theme.button_color = v.primary_color;
  }
  if (v.success_title) theme.success_title = v.success_title;
  if (v.welcome_title) {
    theme.welcome = { ...(theme.welcome || {}), title: v.welcome_title };
  }
  page.value = { ...page.value, name: v.name || page.value.name, theme };
};

const flushEvents = () => {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (!pendingEvents.length) return;
  const events = pendingEvents.splice(0, pendingEvents.length);
  trackFormEvents({
    slug,
    session: sessionToken,
    events,
    hp: hp.value,
    variant: activeVariantKey.value,
  }).catch(() => {});
};

const track = (type, extra = {}) => {
  if (!slug) return;
  pendingEvents.push({ type, ...extra });
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flushEvents, 900);
};

// Primeiro toque real no formulário = "start" (uma vez por sessão).
const trackStart = () => {
  if (startTracked.value) return;
  startTracked.value = true;
  track('start');
};

const flushEventsBeacon = () => {
  if (!pendingEvents.length) return;
  if (typeof navigator?.sendBeacon !== 'function') {
    flushEvents();
    return;
  }
  const events = pendingEvents.splice(0, pendingEvents.length);
  const blob = new Blob(
    [
      JSON.stringify({
        session: sessionToken,
        events,
        hp: hp.value,
        variant: activeVariantKey.value,
      }),
    ],
    { type: 'application/json' }
  );
  navigator.sendBeacon(formEventsUrl(slug), blob);
};

const onPartialVisibilityChange = () => {
  if (document.hidden) {
    savePartialBeacon();
    flushEventsBeacon();
  }
};
const onPartialBeforeUnload = () => {
  savePartialBeacon();
  flushEventsBeacon();
};

const submit = async () => {
  if (submitting.value) return;
  banner.value = null;
  if (!validate()) return;

  submitting.value = true;
  // event_id compartilhado entre o Pixel (browser) e o CAPI (server) para a
  // Meta deduplicar a conversão de Lead (evita contar em dobro).
  const metaEventId =
    (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) ||
    `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  try {
    const data = await submitForm({
      slug,
      fields: respostasValidas(),
      hp: hp.value,
      tracking: { ...tracking.value, event_id: metaEventId },
    });
    fullySubmitted.value = true;
    if (window.fbq) {
      window.fbq(
        'track',
        page.value?.meta_event_name || 'Lead',
        {},
        { eventID: metaEventId }
      );
    }
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'chatsuite_form_submit',
      form_slug: slug,
      event_id: metaEventId,
    });
    if (window.gtag) {
      window.gtag('event', 'generate_lead', {
        form_slug: slug,
        event_id: metaEventId,
      });
    }
    // Antes do desvio de redirect abaixo: no modo redirect a página some, e o
    // aviso não sairia.
    notifyHost({ event: 'submitted', eventId: metaEventId });
    track('submit');
    flushEvents();
    const success = data.success || {};
    if (success.mode === 'redirect' && success.redirect_url) {
      window.location.href = success.redirect_url;
      return;
    }
    successMessage.value = success.message || DEFAULT_SUCCESS_MESSAGE;
    agendamento.value = data.scheduling || null;
    pagamento.value = data.payment || null;
    step.value = 'success';
    acompanharPix();
  } catch (error) {
    const status = error?.response?.status;
    if (status === 429) {
      banner.value = {
        type: 'rate_limit',
        message:
          error.response?.data?.error ||
          'Muitos envios recentes. Aguarde um pouco e tente novamente.',
      };
    } else if (status === 422) {
      banner.value = {
        type: 'error',
        message:
          'Não foi possível enviar seus dados. Revise os campos e tente novamente.',
      };
    } else {
      banner.value = {
        type: 'error',
        message:
          'Algo deu errado ao enviar o formulário. Tente novamente em instantes.',
      };
    }
  } finally {
    submitting.value = false;
  }
};

// Caminho ÚNICO de avanço de passo. Existem dois gatilhos (botão/Enter e o
// auto-avanço das perguntas de escolha única), e os dois precisam emitir
// step_completed + salvar o parcial — senão o funil de "Resultados" mostra 0
// em todo passo que avança sozinho.
const advanceStep = () => {
  // Captura o índice ANTES de avançar: step_completed marca o passo concluído.
  const leaving = stepIndex.value;
  // A pergunta vai junto: o índice conta só os passos VISÍVEIS desta sessão,
  // então numa jornada com perguntas condicionais ele aponta para a pergunta
  // errada no relatório. A chave é o que identifica o passo de verdade.
  const leavingKey = currentStepFields.value[0]?.key;
  if (!goNextStep()) return false;
  track('step_completed', { step_index: leaving, field_key: leavingKey });
  savePartial();
  return true;
};

// Passo a passo: ao selecionar a resposta de uma pergunta de ESCOLHA ÚNICA
// (radio/select) que é o último campo do passo, avança sozinho — com um pequeno
// respiro pra o usuário ver a seleção. Não vale pra checkbox/texto nem no último
// passo (que tem o botão Enviar).
const onStepFieldSelect = field => {
  if (!['select', 'radio'].includes(field.type)) return;
  const stepFields = currentStepFields.value;
  if (stepFields[stepFields.length - 1]?.key !== field.key) return;
  if (isLastFormStep.value) return;
  setTimeout(() => advanceStep(), 250);
};

// On the steps layout, Enter inside a field must advance to the next step
// instead of submitting the whole form early — only the final step's Enter
// (or its Enviar button) reaches submit().
const onFormSubmit = () => {
  if (isStepsLayout.value && !isLastFormStep.value) {
    advanceStep();
    return;
  }
  submit();
};

onMounted(async () => {
  document.addEventListener('visibilitychange', onPartialVisibilityChange);
  window.addEventListener('beforeunload', onPartialBeforeUnload);

  if (!slug) {
    step.value = 'invalid';
    return;
  }
  try {
    page.value = await getFormPage(slug);
    tracking.value = captureTracking(page.value?.tracking_config || {});
    applyVariant();
    initializeValues();
    resetSteps();
    showWelcome.value = !!welcome.value.enabled;
    if (page.value?.name) document.title = page.value.name;
    step.value = 'form';
    track('view');
    // Sem capa + passo a passo: já foca o primeiro campo pra sair digitando.
    if (!showWelcome.value && isStepsLayout.value) focusStepInput();
  } catch (error) {
    step.value = 'invalid';
  }
  requestAnimationFrame(() => {
    mounted.value = true;
  });
});

onUnmounted(() => {
  document.removeEventListener('visibilitychange', onPartialVisibilityChange);
  window.removeEventListener('beforeunload', onPartialBeforeUnload);
});
</script>

<template>
  <div
    class="csf-page"
    :style="step === 'form' || step === 'success' ? themeStyle : {}"
  >
    <div v-if="step === 'loading'" class="csf-center">
      <div class="csf-spinner" aria-hidden="true" />
      <p class="csf-muted">Carregando formulário…</p>
    </div>

    <div v-else-if="step === 'invalid'" class="csf-center">
      <div class="csf-card csf-card--static">
        <h1 class="csf-title">Formulário indisponível</h1>
        <p class="csf-muted">
          Este link não existe ou não está mais ativo. Verifique o endereço com
          quem compartilhou o link.
        </p>
      </div>
    </div>

    <!-- Success screen: full-bleed, no card — sits directly on the theme
         background (like .csf-welcome/.csf-flow below) so the title/message
         stay readable in ANY theme, light or dark. A boxed card here used to
         mix theme.card_color with theme.text_color, which broke on dark
         themes (white text on a light card). Title is customizable via
         theme.success_title (JSONB, no migration — same trick as
         theme.welcome above). -->
    <div v-else-if="step === 'success'" class="csf-success">
      <div class="csf-success__inner" :class="{ 'is-in': mounted }">
        <div class="csf-check" aria-hidden="true">
          <svg viewBox="0 0 24 24" fill="none">
            <path
              class="csf-check__path"
              d="M20 6 9 17l-5-5"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <h1 class="csf-success__title">{{ successTitle }}</h1>
        <p class="csf-success__desc">{{ successMessage }}</p>
        <p v-if="agendamentoTexto" class="csf-success__sched">
          {{ agendamentoTexto }}
        </p>

        <div v-if="pagamento" class="csf-pix">
          <p v-if="pagamento.status === 'pending'" class="csf-pix__msg">
            Gerando o seu Pix{{ pixValor ? ` de ${pixValor}` : '' }}…
          </p>
          <template v-else-if="pagamento.status === 'ready'">
            <p class="csf-pix__valor">Pague {{ pixValor }} com Pix</p>
            <img
              v-if="pagamento.pix_qr_code"
              class="csf-pix__qr"
              :src="`data:image/png;base64,${pagamento.pix_qr_code}`"
              alt="QR Code do Pix"
            />
            <button type="button" class="csf-pix__copiar" @click="copiarPix">
              {{ pixCopiado ? 'Código copiado!' : 'Copiar código Pix' }}
            </button>
          </template>
          <p v-else class="csf-pix__msg">
            Não consegui gerar o Pix agora. Vamos te enviar a cobrança pelo
            WhatsApp.
          </p>
        </div>
      </div>
    </div>

    <!-- Optional welcome/cover screen (theme.welcome.enabled), SpotForm/
         Typeform style: full-bleed, no card, precedes both the single and
         the steps layout below. -->
    <div v-else-if="showWelcome" class="csf-welcome">
      <div class="csf-welcome__inner" :class="{ 'is-in': mounted }">
        <img v-if="logoUrl" :src="logoUrl" alt="" class="csf-welcome__logo" />
        <img
          v-if="headerImageUrl"
          :src="headerImageUrl"
          alt=""
          class="csf-welcome__cover"
        />
        <h1 class="csf-welcome__title">{{ welcome.title || page.name }}</h1>
        <p v-if="welcome.description" class="csf-welcome__desc">
          {{ welcome.description }}
        </p>
        <button
          type="button"
          class="csf-welcome__button"
          @click="dismissWelcome"
        >
          {{ welcomeButtonLabel }}
          <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M5 12h14m0 0-6-6m6 6-6 6"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
        <span class="csf-welcome__hint">pressione Enter ↵</span>
      </div>
    </div>

    <!-- Steps (Typeform-style) layout: full-bleed, one question per screen,
         no card — everything floats on the theme background. -->
    <template v-else-if="isStepsLayout">
      <div
        v-if="totalSteps > 1"
        class="csf-topbar"
        role="progressbar"
        :aria-valuenow="stepIndex + 1"
        :aria-valuemin="1"
        :aria-valuemax="totalSteps"
      >
        <span
          class="csf-topbar__fill"
          :style="{ width: `${stepProgress * 100}%` }"
        />
      </div>

      <form
        ref="stepFormRef"
        class="csf-flow"
        novalidate
        @submit.prevent="onFormSubmit"
        @input="trackStart"
      >
        <div class="csf-flow__header">
          <img v-if="logoUrl" :src="logoUrl" alt="" class="csf-flow__logo" />
          <span v-if="totalSteps > 1" class="csf-flow__count">
            {{ stepIndex + 1 }} de {{ totalSteps }}
          </span>
        </div>

        <div class="csf-flow__center">
          <Transition
            name="csf-step"
            mode="out-in"
            @after-enter="focusStepInput"
          >
            <div :key="stepIndex" class="csf-flow__step">
              <img
                v-if="formHeaderImageUrl && isFirstFormStep"
                :src="formHeaderImageUrl"
                alt=""
                class="csf-flow__cover"
              />

              <!-- One-field-per-step is the common case, and keeps its
                   existing "big question" markup unchanged (fieldIdx is
                   always 0 then). A step with MULTIPLE fields — because a
                   later field opted into group_with_previous — keeps that
                   same big-question treatment for the FIRST field only; the
                   rest stack below as clean, deliberately smaller sub-fields
                   (.csf-qmore) instead of repeating the giant step number
                   for each one. -->
              <template
                v-for="(field, fieldIdx) in currentStepFields"
                :key="field.key"
              >
                <div
                  v-if="field.type !== 'consent' && fieldIdx === 0"
                  class="csf-qhead"
                >
                  <span class="csf-qnum" aria-hidden="true">
                    {{ stepIndex + 1 }}
                    <svg
                      class="csf-qnum__arrow"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M7 4v7a4 4 0 0 0 4 4h6m0 0-3.5-3.5M17 15l-3.5 3.5"
                        stroke="currentColor"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </span>
                  <div class="csf-qbody">
                    <h2 class="csf-qtitle">
                      {{ field.label }}
                      <span v-if="field.required" class="csf-qrequired">*</span>
                    </h2>
                    <p v-if="stepHelperText(field)" class="csf-qhelp">
                      {{ stepHelperText(field) }}
                    </p>
                    <FormField
                      v-model="values[field.key]"
                      :field="bareField(field)"
                      :error="errors[field.key]"
                      variant="bare"
                      @update:model-value="onStepFieldSelect(field)"
                    />
                  </div>
                </div>

                <div v-else-if="field.type !== 'consent'" class="csf-qmore">
                  <label
                    class="csf-qmore__label"
                    :for="`csf-field-${field.key}`"
                    :data-required="field.required"
                  >
                    {{ field.label }}
                  </label>
                  <p v-if="stepHelperText(field)" class="csf-qmore__help">
                    {{ stepHelperText(field) }}
                  </p>
                  <FormField
                    v-model="values[field.key]"
                    :field="bareField(field)"
                    :error="errors[field.key]"
                    variant="bare"
                    @update:model-value="onStepFieldSelect(field)"
                  />
                </div>

                <FormField
                  v-else
                  v-model="values[field.key]"
                  :field="field"
                  :error="errors[field.key]"
                  variant="bare"
                />
              </template>

              <template v-if="isLastFormStep">
                <label v-if="showStandaloneConsent" class="csf-consent">
                  <input
                    v-model="values[CONSENT_FALLBACK_KEY]"
                    type="checkbox"
                    class="csf-consent__input"
                  />
                  <span class="csf-consent__text">{{ consentText }}</span>
                </label>
                <p v-if="errors[CONSENT_FALLBACK_KEY]" class="csf-error">
                  {{ errors[CONSENT_FALLBACK_KEY] }}
                </p>
              </template>

              <div class="csf-oknav">
                <button
                  v-if="!isFirstFormStep"
                  type="button"
                  class="csf-back"
                  @click="goPrevStep"
                >
                  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path
                      d="M15 6l-6 6 6 6"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  Voltar
                </button>

                <template v-if="!isLastFormStep">
                  <button type="submit" class="csf-ok">
                    Ok
                    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                      <path
                        d="M20 6 9 17l-5-5"
                        stroke="currentColor"
                        stroke-width="2.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </button>
                  <span class="csf-oknav__hint">pressione Enter ↵</span>
                </template>

                <button
                  v-else
                  type="submit"
                  class="csf-ok"
                  :disabled="submitting"
                  :aria-busy="submitting"
                >
                  <span
                    v-if="submitting"
                    class="csf-btn-spinner"
                    aria-hidden="true"
                  />
                  {{ submitting ? 'Enviando…' : 'Enviar' }}
                </button>
              </div>

              <div
                v-if="banner"
                class="csf-banner"
                :class="`csf-banner--${banner.type}`"
              >
                {{ banner.message }}
              </div>
            </div>
          </Transition>
        </div>

        <!-- Honeypot: hidden from real visitors, invisible to screen readers, left
             empty by humans. Any bot that autofills it flips this field non-blank. -->
        <div class="csf-honeypot" aria-hidden="true">
          <label for="csf-website">Website</label>
          <input
            id="csf-website"
            v-model="hp"
            type="text"
            tabindex="-1"
            autocomplete="off"
          />
        </div>
      </form>
    </template>

    <!-- Single-page layout: unchanged boxed card experience. -->
    <div v-else class="csf-center">
      <form
        class="csf-card"
        :class="{ 'is-in': mounted }"
        novalidate
        @submit.prevent="onFormSubmit"
        @input="trackStart"
      >
        <div class="csf-accent" aria-hidden="true" />
        <img
          v-if="formHeaderImageUrl"
          :src="formHeaderImageUrl"
          alt=""
          class="csf-cover"
        />

        <div class="csf-card__body">
          <img v-if="logoUrl" :src="logoUrl" alt="" class="csf-logo" />
          <h1 class="csf-title">{{ page.name }}</h1>

          <FormField
            v-for="field in visibleFields"
            :key="field.key"
            v-model="values[field.key]"
            :field="field"
            :error="errors[field.key]"
          />

          <label v-if="showStandaloneConsent" class="csf-consent">
            <input
              v-model="values[CONSENT_FALLBACK_KEY]"
              type="checkbox"
              class="csf-consent__input"
            />
            <span class="csf-consent__text">{{ consentText }}</span>
          </label>
          <p v-if="errors[CONSENT_FALLBACK_KEY]" class="csf-error">
            {{ errors[CONSENT_FALLBACK_KEY] }}
          </p>

          <!-- Honeypot: hidden from real visitors, invisible to screen readers, left
               empty by humans. Any bot that autofills it flips this field non-blank. -->
          <div class="csf-honeypot" aria-hidden="true">
            <label for="csf-website">Website</label>
            <input
              id="csf-website"
              v-model="hp"
              type="text"
              tabindex="-1"
              autocomplete="off"
            />
          </div>

          <div
            v-if="banner"
            class="csf-banner"
            :class="`csf-banner--${banner.type}`"
          >
            {{ banner.message }}
          </div>

          <button
            type="submit"
            class="csf-submit"
            :disabled="submitting"
            :aria-busy="submitting"
          >
            <span
              v-if="submitting"
              class="csf-btn-spinner"
              aria-hidden="true"
            />
            {{ submitting ? 'Enviando…' : 'Enviar' }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<style>
/* Reset global — a página hospedada é um <body> próprio e o CSS scoped não
 * alcança html/body. O margin padrão do body (8px) estava mostrando uma
 * MOLDURA BRANCA em volta da página full-bleed e forçando um SCROLL de 8px.
 * Também garante 100% de altura sem barra desnecessária. */
html,
body {
  margin: 0;
  padding: 0;
  height: 100%;
}
body {
  overflow-x: hidden;
}
</style>

<style scoped>
.csf-page {
  min-height: 100vh;
  width: 100%;
  box-sizing: border-box;
  background: var(--csf-bg, #f5f4ef);
  font-family: var(
    --csf-font,
    -apple-system,
    BlinkMacSystemFont,
    'Segoe UI',
    Roboto,
    sans-serif
  );
  color: var(--csf-text, #1b1b18);
  -webkit-font-smoothing: antialiased;
}

.csf-page * {
  box-sizing: border-box;
}

.csf-center {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 28px 16px;
}

.csf-card {
  position: relative;
  width: 100%;
  max-width: 560px;
  background: var(--csf-card-bg, #fff);
  border-radius: var(--csf-radius-lg, 22px);
  box-shadow:
    0 1px 2px rgba(20, 20, 15, 0.04),
    0 18px 40px -16px rgba(20, 20, 15, 0.18);
  overflow: hidden;
  opacity: 0;
  transform: translateY(10px);
  transition:
    opacity 0.45s ease,
    transform 0.45s ease;
}

.csf-card.is-in {
  opacity: 1;
  transform: translateY(0);
}

.csf-card--static {
  padding: 36px 30px;
  text-align: center;
}

.csf-accent {
  height: 4px;
  width: 100%;
  background: linear-gradient(
    90deg,
    var(--csf-primary, #146b57),
    color-mix(
      in srgb,
      var(--csf-primary, #146b57) 55%,
      var(--csf-button, #146b57)
    )
  );
}

.csf-cover {
  display: block;
  width: 100%;
  max-height: var(--csf-header-max-height, 150px);
  object-fit: contain;
}

.csf-card__body {
  padding: 30px 28px 32px;
}

.csf-logo {
  display: block;
  height: 36px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
  margin-bottom: 16px;
}

.csf-title {
  margin: 0 0 8px;
  font-size: 22px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.25;
  color: var(--csf-text, #1b1b18);
}

.csf-card--static .csf-title {
  margin-top: 4px;
}

.csf-step-enter-active,
.csf-step-leave-active {
  transition:
    opacity 0.22s ease,
    transform 0.22s ease;
}

.csf-step-enter-from {
  opacity: 0;
  transform: translateX(16px);
}

.csf-step-leave-to {
  opacity: 0;
  transform: translateX(-16px);
}

/* ---- Welcome / cover screen (theme.welcome) ---------------------------
 * Full-bleed intro, same visual language as the steps layout below (no
 * card, spacious, centered) — shown before either the single or the steps
 * form experience. */

.csf-welcome {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.csf-welcome__inner {
  width: 100%;
  max-width: 620px;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0;
  transform: translateY(14px);
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
}

.csf-welcome__inner.is-in {
  opacity: 1;
  transform: translateY(0);
}

.csf-welcome__logo {
  display: block;
  height: 40px;
  width: auto;
  max-width: 220px;
  object-fit: contain;
  margin-bottom: 28px;
}

.csf-welcome__cover {
  display: block;
  width: auto;
  max-width: 100%;
  max-height: var(--csf-header-max-height, 150px);
  object-fit: contain;
  margin-bottom: 28px;
}

.csf-welcome__title {
  margin: 0;
  font-size: clamp(28px, 6vw, 44px);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--csf-text, #1b1b18);
}

.csf-welcome__desc {
  margin: 16px 0 0;
  max-width: 480px;
  font-size: clamp(15px, 2.4vw, 17px);
  line-height: 1.6;
  color: color-mix(in srgb, var(--csf-text, #1b1b18) 62%, transparent);
}

.csf-welcome__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 32px;
  min-height: 52px;
  padding: 0 30px;
  border: none;
  border-radius: var(--csf-radius, 16px);
  background: var(--csf-button, #146b57);
  color: var(--csf-button-text, #fff);
  font-size: 16px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition:
    filter 0.15s ease,
    transform 0.05s ease;
}

.csf-welcome__button svg {
  width: 18px;
  height: 18px;
}

.csf-welcome__button:hover {
  filter: brightness(0.94);
}

.csf-welcome__button:active {
  transform: scale(0.98);
}

.csf-welcome__button:focus-visible {
  outline: 3px solid
    color-mix(in srgb, var(--csf-primary, #146b57) 45%, transparent);
  outline-offset: 2px;
}

.csf-welcome__hint {
  margin-top: 14px;
  font-size: 13px;
  color: color-mix(in srgb, var(--csf-text, #1b1b18) 40%, transparent);
}

@media (max-width: 480px) {
  .csf-welcome__hint {
    display: none;
  }
}

/* ---- Success screen ----------------------------------------------------
 * Full-bleed, same visual language as .csf-welcome above: no card, spacious,
 * centered on the theme background. Title/message use --csf-text and the
 * check accent uses --csf-primary, so the screen reads correctly whether
 * the account's theme is light or dark. */

.csf-success {
  min-height: 100vh;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  text-align: center;
}

.csf-success__inner {
  width: 100%;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
  opacity: 0;
  transform: translateY(14px);
  transition:
    opacity 0.5s ease,
    transform 0.5s ease;
}

.csf-success__inner.is-in {
  opacity: 1;
  transform: translateY(0);
}

.csf-success__title {
  margin: 0;
  font-size: clamp(28px, 6vw, 40px);
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.2;
  color: var(--csf-text, #1b1b18);
}

.csf-pix {
  margin: 18px auto 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  max-width: 280px;
}

.csf-pix__msg {
  margin: 0;
  font-size: 14px;
  opacity: 0.8;
}

.csf-pix__valor {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
}

.csf-pix__qr {
  width: 200px;
  height: 200px;
  border-radius: 12px;
  background: #fff;
}

.csf-pix__copiar {
  padding: 11px 18px;
  border: none;
  border-radius: var(--csf-radius);
  background: var(--csf-primary);
  color: #fff;
  font-family: var(--csf-font);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.csf-success__sched {
  margin: 10px 0 0;
  font-size: 15px;
  font-weight: 600;
}

.csf-success__desc {
  margin: 14px 0 0;
  max-width: 440px;
  font-size: clamp(15px, 2.4vw, 17px);
  line-height: 1.6;
  color: color-mix(in srgb, var(--csf-text, #1b1b18) 62%, transparent);
}

/* ---- Steps (Typeform-style) full-bleed layout ------------------------- */

.csf-topbar {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10;
  width: 100%;
  height: 3px;
  background: color-mix(in srgb, var(--csf-text, #1b1b18) 10%, transparent);
}

.csf-topbar__fill {
  display: block;
  height: 100%;
  background: var(--csf-primary, #146b57);
  transition: width 0.3s ease;
}

.csf-flow {
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 20px 20px 28px;
}

.csf-flow__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  min-height: 30px;
}

.csf-flow__logo {
  display: block;
  height: 28px;
  width: auto;
  max-width: 160px;
  object-fit: contain;
}

.csf-flow__count {
  margin-left: auto;
  font-size: 12.5px;
  font-weight: 600;
  letter-spacing: 0.02em;
  color: color-mix(in srgb, var(--csf-text, #1b1b18) 50%, transparent);
}

.csf-flow__center {
  flex: 1;
  display: flex;
  align-items: center;
  padding: 32px 0;
}

.csf-flow__step {
  width: 100%;
  max-width: 640px;
  margin: 0 auto;
}

.csf-flow__cover {
  display: block;
  width: 100%;
  max-height: var(--csf-header-max-height, 150px);
  object-fit: contain;
  border-radius: var(--csf-radius-lg, 22px);
  margin-bottom: 24px;
}

.csf-qhead {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.csf-qnum {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding-top: 0.2em;
  font-size: 17px;
  font-weight: 700;
  color: var(--csf-primary, #146b57);
}

.csf-qnum__arrow {
  width: 16px;
  height: 16px;
}

.csf-qbody {
  flex: 1 1 260px;
  min-width: 0;
}

.csf-qtitle {
  margin: 0;
  font-size: clamp(22px, 5vw, 32px);
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.3;
  color: var(--csf-text, #1b1b18);
}

.csf-qrequired {
  margin-left: 2px;
  color: var(--csf-primary, #146b57);
}

.csf-qhelp {
  margin: 8px 0 0;
  font-size: 15px;
  line-height: 1.5;
  color: color-mix(in srgb, var(--csf-text, #1b1b18) 62%, transparent);
}

.csf-qbody > .csf-field {
  margin-top: 22px;
}

/* ---- Grouped step sub-fields (field.group_with_previous) --------------
 * When a step ends up with more than one field, the first keeps the full
 * "big question" treatment above (.csf-qhead/.csf-qnum/.csf-qtitle). The
 * rest stack below it here — same underline/bare input language, still
 * full-bleed and on-brand, but with a smaller label instead of repeating
 * the giant step number/title for every field. */
.csf-qmore {
  margin-top: 28px;
}

.csf-qmore__label {
  display: block;
  margin-bottom: 8px;
  font-size: 15px;
  font-weight: 600;
  letter-spacing: -0.005em;
  line-height: 1.35;
  color: var(--csf-text, #1b1b18);
}

.csf-qmore__label[data-required='true']::after {
  content: ' *';
  color: var(--csf-primary, #146b57);
}

.csf-qmore__help {
  margin: 6px 0 0;
  font-size: 13.5px;
  line-height: 1.5;
  color: color-mix(in srgb, var(--csf-text, #1b1b18) 62%, transparent);
}

.csf-qmore > .csf-field {
  margin-top: 10px;
}

.csf-oknav {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  margin-top: 30px;
}

.csf-back {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 2px;
  border: none;
  background: transparent;
  color: color-mix(in srgb, var(--csf-text, #1b1b18) 55%, transparent);
  font-size: 14px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
}

.csf-back svg {
  width: 16px;
  height: 16px;
}

.csf-back:hover {
  color: var(--csf-text, #1b1b18);
}

.csf-ok {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 0 22px;
  border: none;
  border-radius: var(--csf-radius, 16px);
  background: var(--csf-button, #146b57);
  color: var(--csf-button-text, #fff);
  font-size: 15px;
  font-weight: 600;
  font-family: inherit;
  cursor: pointer;
  transition:
    filter 0.15s ease,
    transform 0.05s ease;
}

.csf-ok svg {
  width: 16px;
  height: 16px;
}

.csf-ok:hover:not(:disabled) {
  filter: brightness(0.94);
}

.csf-ok:active:not(:disabled) {
  transform: scale(0.98);
}

.csf-ok:disabled {
  cursor: default;
  opacity: 0.75;
}

.csf-back:focus-visible,
.csf-ok:focus-visible {
  outline: 3px solid
    color-mix(in srgb, var(--csf-primary, #146b57) 45%, transparent);
  outline-offset: 2px;
}

.csf-oknav__hint {
  font-size: 13px;
  color: color-mix(in srgb, var(--csf-text, #1b1b18) 40%, transparent);
}

.csf-flow .csf-consent {
  background: transparent;
  border: none;
  border-bottom: 1px solid
    color-mix(in srgb, var(--csf-text, #1b1b18) 14%, transparent);
  border-radius: 0;
  padding: 12px 2px;
  margin-top: 26px;
}

.csf-flow .csf-banner {
  margin-top: 20px;
}

@media (max-width: 480px) {
  .csf-oknav__hint {
    display: none;
  }
}

.csf-muted {
  margin: 0;
  font-size: 14.5px;
  line-height: 1.55;
  color: rgba(27, 27, 24, 0.62);
}

.csf-card__body > .csf-muted {
  margin-bottom: 22px;
}

.csf-consent {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  margin-top: 4px;
  margin-bottom: 4px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: calc(var(--csf-radius, 16px) * 0.7);
  cursor: pointer;
  font-size: 13.5px;
  line-height: 1.5;
}

.csf-consent__input {
  margin-top: 2px;
  width: 17px;
  height: 17px;
  flex-shrink: 0;
  accent-color: var(--csf-primary, #146b57);
}

.csf-error {
  margin: 6px 0 0;
  font-size: 12.5px;
  color: #b3261e;
}

.csf-honeypot {
  position: absolute;
  left: -9999px;
  width: 1px;
  height: 1px;
  overflow: hidden;
}

.csf-banner {
  margin: 18px 0 0;
  padding: 11px 14px;
  border-radius: calc(var(--csf-radius, 16px) * 0.6);
  font-size: 13.5px;
  line-height: 1.5;
}

.csf-banner--error {
  background: #fbeae8;
  color: #8c2c22;
}

.csf-banner--rate_limit {
  background: #fbf3e0;
  color: #7a5314;
}

.csf-submit {
  width: 100%;
  margin-top: 22px;
  min-height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: none;
  border-radius: var(--csf-radius, 16px);
  background: var(--csf-button, #146b57);
  color: var(--csf-button-text, #fff);
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition:
    filter 0.15s ease,
    transform 0.05s ease;
}

.csf-submit:hover:not(:disabled) {
  filter: brightness(0.94);
}

.csf-submit:active:not(:disabled) {
  transform: scale(0.99);
}

.csf-submit:focus-visible {
  outline: 3px solid
    color-mix(in srgb, var(--csf-primary, #146b57) 45%, transparent);
  outline-offset: 2px;
}

.csf-submit:disabled {
  cursor: default;
  opacity: 0.75;
}

.csf-btn-spinner {
  width: 15px;
  height: 15px;
  border-radius: 50%;
  border: 2px solid
    color-mix(in srgb, var(--csf-button-text, #fff) 35%, transparent);
  border-top-color: var(--csf-button-text, #fff);
  animation: csf-spin 0.7s linear infinite;
}

.csf-spinner {
  width: 26px;
  height: 26px;
  margin: 0 auto 14px;
  border-radius: 50%;
  border: 3px solid rgba(20, 20, 15, 0.12);
  border-top-color: rgba(20, 20, 15, 0.45);
  animation: csf-spin 0.8s linear infinite;
}

.csf-check {
  width: 60px;
  height: 60px;
  margin: 0 auto 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  /* Mixed with --csf-bg (not a fixed white) so the tint stays visible on
   * both light and dark themes instead of turning into a near-invisible
   * pale disc on a dark background. */
  background: color-mix(
    in srgb,
    var(--csf-primary, #146b57) 16%,
    var(--csf-bg, #f5f4ef)
  );
  color: var(--csf-primary, #146b57);
}

.csf-check svg {
  width: 28px;
  height: 28px;
}

.csf-check__path {
  stroke-dasharray: 24;
  stroke-dashoffset: 24;
  animation: csf-draw 0.5s ease 0.15s forwards;
}

@keyframes csf-spin {
  to {
    transform: rotate(360deg);
  }
}

@keyframes csf-draw {
  to {
    stroke-dashoffset: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .csf-card,
  .csf-welcome__inner,
  .csf-success__inner,
  .csf-btn-spinner,
  .csf-spinner,
  .csf-check__path,
  .csf-topbar__fill,
  .csf-step-enter-active,
  .csf-step-leave-active {
    animation: none !important;
    transition: none !important;
    opacity: 1 !important;
    transform: none !important;
    stroke-dashoffset: 0 !important;
  }
}

@media (max-width: 480px) {
  .csf-center {
    padding: 0;
  }

  .csf-card {
    max-width: 100%;
    min-height: 100vh;
    border-radius: 0;
  }

  .csf-card--static {
    min-height: 100vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  .csf-success {
    padding: 28px 16px;
  }

  .csf-flow {
    padding: 16px 20px 24px;
  }

  .csf-flow__center {
    padding: 20px 0;
  }
}
</style>
